<script setup lang="ts" generic="T extends { label: string; [key: string]: string | number | boolean | Object }">
import { useSlots } from 'vue'

import LoadingIcon from '@/assets/icons/loading.svg?component'
import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

withDefaults(
  defineProps<{
    title?: string
    placeholder?: string
    options: T[]
    valueKey?: string
    isLoading?: boolean
    isFetching?: boolean
    clearable?: boolean
    filterable?: boolean
    multiple?: boolean
    remote?: boolean
    remoteMethod?: (str: string) => void
    extraClass?: string
    popperClass?: string
    disabled?: boolean
    teleported?: boolean
    allowCreate?: boolean
    defaultFirstOption?: boolean
    reserveKeyword?: boolean
    onItemSelect?: (item: T) => void
    onClear?: () => void
    name?: string
    size?: 'default' | 'small' | 'large'
  }>(),
  {
    title: undefined,
    placeholder: 'Select',
    valueKey: 'value',
    clearable: false,
    multiple: false,
    remote: false,
    filterable: false,
    isLoading: false,
    isFetching: false,
    remoteMethod: () => {
      // empty
    },
    extraClass: '',
    popperClass: undefined,
    disabled: false,
    teleported: true,
    allowCreate: false,
    defaultFirstOption: false,
    reserveKeyword: true,
    onItemSelect: undefined,
    onClear: undefined,
    name: undefined,
    size: 'large',
  },
)
defineSlots<{ label(): void; header(): void; footer(): void; option(data: { item: T }): void }>()
const slots = useSlots()

const value = defineModel<string | number | unknown[]>('modelValue', { default: '' })
</script>
<template>
  <BoxComponent>
    <BoxComponent v-if="title" :mb="8">
      <TypographyComponent variant="title-large">{{ title }}</TypographyComponent>
    </BoxComponent>
    <slot name="header" />
    <FlexComponent v-if="isFetching" justify="center">
      <el-icon :size="56">
        <LoadingIcon />
      </el-icon>
    </FlexComponent>
    <el-select
      v-else
      v-model="value"
      :placeholder="placeholder"
      :clearable="clearable"
      :loading="isLoading"
      :multiple="multiple"
      :remote="remote"
      :filterable="filterable"
      :allow-create="allowCreate"
      :default-first-option="defaultFirstOption"
      :reserve-keyword="reserveKeyword"
      :value-key="valueKey"
      :remote-method="remoteMethod as () => void"
      :size="size"
      :class="[{ 'el-select__selection-small-paddings': filterable }, extraClass]"
      :disabled="disabled"
      :teleported="teleported"
      :popper-class="popperClass"
      :name="name"
      @clear="onClear"
    >
      <!-- @vue-expect-error todo fix me -->
      <el-option
        v-for="item in options"
        :key="item.label + item[valueKey]"
        :label="item.label"
        :value="valueKey === 'value' ? item.value : item"
        :class="{ 'select-component--option-nested': item.nestedItem }"
        @click="onItemSelect ? onItemSelect(item) : undefined"
      >
        <slot name="option" :item="item" />
      </el-option>
      <template #label>
        <slot name="label" />
      </template>
      <template v-if="slots.footer" #footer>
        <slot name="footer" />
      </template>
      <template #loading>
        <LoadingComponent />
      </template>
      <template #empty>
        <TypographyComponent variant="body-small" text-align="center">No matching data</TypographyComponent>
      </template>
    </el-select>
  </BoxComponent>
</template>
<style lang="scss" scoped>
:deep(.el-select) {
  --el-select-border-color-hover: var(--secondary-64);
  --el-select-disabled-color: var(--secondary-18);
  --el-select-disabled-border: var(--secondary-18);
  --el-select-font-size: 14px;
  --el-select-close-hover-color: var(--secondary-64);
  --el-select-input-color: var(--primary);
  --el-select-multiple-input-color: var(--primary);
  --el-select-input-focus-border-color: var(--secondary-12);
  --el-select-input-font-size: 14px;
  border: none !important;
}

:deep(.el-select__wrapper) {
  height: 56px;
  padding: 15px 16px;
  border: none;
  box-shadow: 0 0 0 1px var(--secondary-12) inset;
  border-radius: 8px;

  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  background: var(--inverted);

  &.is-focused,
  &.is-hovering {
    box-shadow: 0 0 0 1px var(--secondary-64) inset;
  }

  &.is-filterable {
    padding: 8px 16px;

    .el-select__selection {
      padding: 7px 0;

      &.is-near {
        padding: 2px 0;
      }
    }
  }

  &.is-disabled {
    cursor: not-allowed;
    box-shadow: 0 0 0 1px var(--secondary-18) inset;
    background: linear-gradient(0deg, rgba(34, 34, 34, 0.02) 0%, rgba(34, 34, 34, 0.02) 100%), #fff;

    .el-select__caret {
      color: var(--secondary-18);
    }
  }
}
:deep(.el-select__selection) {
  min-height: 24px;

  .el-tag {
    height: 28px;
    color: var(--primary);
  }
}
:deep(.el-select--small .el-select__wrapper) {
  height: 32px;
  padding: 8px 16px;
}
.select-component--option-nested {
  list-style-type: disc;
  display: list-item;
  padding-left: 24px;
}
</style>
